import React, { useState, useContext } from 'react';
import {
    Typography,
    Button,
    Alert,
    Tag,
    Space,
    message,
    Modal,
    Input,
    Form
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import { dismissEmailAnalysis } from '../../services/emailAnalysisFunctions';
import { loadEmailAttachmentFiles } from '../../services/loadEmailAttachmentFiles';

import FlexCard from '../atoms/FlexCard';
import FlexCol from '../atoms/FlexCol';
import FlexBox from '../atoms/FlexBox';
import { Notification, EmailAnalysisNotificationData } from '#types/System.types';
import { UserContext } from '../../providers/UserContext';
import { AIContext } from '../../providers/AIContext';

const { Text, Title } = Typography;
const { TextArea } = Input;

const MAX_REASON_LENGTH = 256;

interface EmailAnalysisNotificationCardProps {
    notification: Notification;
    isApproved?: boolean;
}


const confidenceColor = (c: number): string => {
    if (c >= 0.8) return 'green';
    if (c >= 0.5) return 'orange';
    return 'red';
};

const EmailAnalysisNotificationCard: React.FC<EmailAnalysisNotificationCardProps> = ({
    notification,
    isApproved = false
}) => {
    const {
        user,
        userRights,
        toggleNotificationRead,
        notificationBundles,
        users
    } = useContext(UserContext);
    const { startDocumentImportRun } = useContext(AIContext);
    const [messageApi, contextHolder] = message.useMessage();

    const notificationData = notification.notificationEvent.data as EmailAnalysisNotificationData | undefined;
    const [localNotificationData, setLocalNotificationData] = useState<EmailAnalysisNotificationData | undefined>(notificationData);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showDismissModal, setShowDismissModal] = useState(false);
    const [dismissReason, setDismissReason] = useState('');

    const getDismissingUserName = (userId: string) => {
        const dismissingUser = users.find(u => u.docId === userId);
        return dismissingUser?.name || userId;
    };

    const getNotificationStatus = () => {
        const eventId = notification.notificationEvent.docId;
        if (!eventId || !notificationBundles) return undefined;
        return notificationBundles.notificationEntryByEventId.get(eventId)?.status;
    };

    const markNotificationRead = async () => {
        const status = getNotificationStatus();
        if (status !== 'read') {
            await toggleNotificationRead(notification);
        }
    };

    const handleReview = async () => {
        if (!notificationData?.analysisId) return;
        setIsLoading(true);
        setError(null);
        try {
            const files = await loadEmailAttachmentFiles(notificationData.analysisId);
            if (files.length === 0) {
                messageApi.error('This email has no importable attachments to review.');
                return;
            }
            await markNotificationRead();
            void startDocumentImportRun(files, notificationData.suggestedFormType ?? 'Quote');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to start review';
            setError(errorMessage);
            messageApi.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDismiss = async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (notificationData?.analysisId) {
                await dismissEmailAnalysis({
                    analysisId: notificationData.analysisId,
                    reason: dismissReason.trim() || undefined,
                });
            }
            await markNotificationRead();
            setLocalNotificationData((prev) => prev ? ({
                ...prev,
                dismissedBy: {
                    userId: user?.uid ?? '',
                    timestamp: Timestamp.now(),
                    reason: dismissReason.trim() || undefined
                }
            }) : prev);
            messageApi.info('Email Analysis was dismissed');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to dismiss notification';
            setError(errorMessage);
            messageApi.error('Failed to dismiss notification');
        } finally {
            setIsLoading(false);
            setShowDismissModal(false);
            setDismissReason('');
        }
    };

    if (!userRights.aiAgent) return null;
    if (!localNotificationData) return null;

    const emailData = localNotificationData;
    const isDismissed = !!emailData.dismissedBy;

    return (
        <FlexCard
            stretch
            gap={16}
            style={{
                opacity: isApproved || isDismissed ? 0.7 : 1
            }}
        >
            {contextHolder}
            <FlexCol gap={8}>
                <Title level={5} style={{ margin: 0 }}>
                    {notification.notificationEvent.message}
                </Title>

                {emailData.intentValue && (
                    <FlexCol gap={4}>
                        <Text italic type="secondary">
                            &ldquo;{emailData.intentValue}&rdquo;
                        </Text>
                        {emailData.intentConfidence != null && (
                            <FlexBox justifyStart noGrow wrap gap={8}>
                                <Tag color={confidenceColor(emailData.intentConfidence)}>
                                    {(emailData.intentConfidence * 100).toFixed(0)}% confidence
                                </Tag>
                                {emailData.lowConfidence && (
                                    <Alert
                                        type="warning"
                                        title="Low confidence — review carefully"
                                        style={{ padding: '2px 8px' }}
                                        banner
                                    />
                                )}
                            </FlexBox>
                        )}
                    </FlexCol>
                )}

                {emailData.project && (
                    <Text>
                        Project: <Tag color="blue">{emailData.project}</Tag>
                    </Text>
                )}

                {emailData.actionItems && emailData.actionItems.length > 0 && (
                    <FlexCol gap={4}>
                        <Text strong>Action Items:</Text>
                        {emailData.actionItems.map((item: string, index: number) => (
                            <Text key={index}>• {item}</Text>
                        ))}
                    </FlexCol>
                )}

                {isDismissed ? (
                    <>
                        <Alert
                            type="info"
                            title={`Email Analysis was dismissed by ${getDismissingUserName(emailData.dismissedBy?.userId || '')}`}
                            description={emailData.dismissedBy?.reason}
                            style={{ backgroundColor: '#fff7e6' }}
                        />
                    </>
                ) : isApproved ? (
                    <Alert
                        type="success"
                        title="Form has been created successfully"
                        showIcon
                    />
                ) : (
                    <>
                        <FlexCol gap={4}>
                            <Text>
                                <Space>
                                    <UserOutlined />
                                    Assigned To:
                                </Space>
                            </Text>
                            <FlexBox wrap gap={8}>
                                <Tag>{emailData.assignedToUser}</Tag>
                                <Text type="secondary" style={{ overflowWrap: 'anywhere' }}>{emailData.assignedToEmail}</Text>
                            </FlexBox>
                        </FlexCol>

                        {error && (
                            <Alert
                                type="error"
                                title={error}
                                showIcon
                            />
                        )}

                        <Space wrap>
                            <Button
                                type="primary"
                                onClick={handleReview}
                                loading={isLoading}
                                aria-label="Review extraction"
                            >
                                Review
                            </Button>
                            <Button
                                onClick={() => setShowDismissModal(true)}
                                loading={isLoading}
                            >
                                Dismiss
                            </Button>
                        </Space>
                    </>
                )}
            </FlexCol>

            <Modal
                title="Dismiss Email Analysis"
                open={showDismissModal}
                onCancel={() => {
                    setShowDismissModal(false);
                    setDismissReason('');
                }}
                onOk={handleDismiss}
                confirmLoading={isLoading}
            >
                <Form>
                    <Form.Item label="Reason (Optional)">
                        <TextArea
                            value={dismissReason}
                            onChange={e => setDismissReason(e.target.value)}
                            placeholder="Why are you dismissing this notification?"
                            maxLength={MAX_REASON_LENGTH}
                            showCount
                            rows={4}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </FlexCard>
    );
};

export default EmailAnalysisNotificationCard;
